import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import WhyThisAction from "./WhyThisAction";

export default function ConsensusPanel({ consensus }) {
  if (!consensus) return null;
  const { weights, ranked_actions = [], top_action, alignment_score, dissenting_opinions = [], formula } = consensus;

  return (
    <div className="cre-surface rounded-sm p-6" data-testid="consensus-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="cre-overline">Weighted consensus</div>
          <h3 className="font-heading text-xl font-bold tracking-tight mt-1">
            Multi-criteria utility
          </h3>
        </div>
        <div className="text-right">
          <div className="cre-overline">Alignment</div>
          <div className="font-mono text-lg font-semibold text-emerald-400">
            {(Number(alignment_score || 0) * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      <div
        className="mt-4 cre-terminal rounded-sm p-3"
        data-testid="consensus-formula"
      >
        <div className="yaml-comment"># Utility function</div>
        <div className="text-zinc-200">{formula}</div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-zinc-400">
          <span>w1 (A) = <span className="yaml-num">{weights.w1}</span></span>
          <span>w2 (P) = <span className="yaml-num">{weights.w2}</span></span>
          <span>w3 (D) = <span className="yaml-num">{weights.w3}</span></span>
          <span>w4 (B) = <span className="yaml-num">{weights.w4}</span></span>
          <span>w5 (1-R) = <span className="yaml-num">{weights.w5}</span></span>
          <span>w6 (1-S) = <span className="yaml-num">{weights.w6}</span></span>
        </div>
      </div>

      {top_action ? (
        <div
          className="mt-4 border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-4"
          data-testid="consensus-top-action"
        >
          <div className="flex items-center justify-between">
            <div className="cre-overline text-emerald-300">Top-ranked action</div>
            <span className="font-mono text-xs text-emerald-300">
              U = {Number(top_action.utility).toFixed(4)}
            </span>
          </div>
          <div className="mt-1 font-medium text-zinc-100">{top_action.action}</div>
          <div className="mt-1 text-xs text-zinc-400">
            Proposed by{" "}
            <span className="text-zinc-200 font-medium">
              {top_action.proposer_agent}
            </span>{" "}
            · {top_action.proposer_domain} ·{" "}
            <span className="uppercase">{top_action.risk} risk</span>
          </div>
          <WhyThisAction consensus={consensus} />
        </div>
      ) : null}

      <div className="mt-5">
        <div className="cre-overline mb-2">Ranked candidate actions</div>
        <div className="space-y-2">
          {ranked_actions.slice(0, 6).map((a, i) => (
            <div
              key={i}
              data-testid={`ranked-action-${i}`}
              className="border border-white/10 rounded-sm p-3"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-xs text-zinc-500 w-6 shrink-0">
                    #{i + 1}
                  </span>
                  <span className="truncate text-sm text-zinc-100">{a.action}</span>
                </div>
                <span className="font-mono text-xs text-blue-300 shrink-0">
                  {Number(a.utility).toFixed(4)}
                </span>
              </div>
              <div className="mt-2">
                <Progress value={Math.min(100, a.utility * 100)} className="h-1" />
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono text-zinc-500">
                {Object.entries(a.component_scores || {}).map(([k, v]) => (
                  <span key={k}>
                    {k}=<span className="text-zinc-300">{v}</span>
                  </span>
                ))}
                <Badge
                  variant="outline"
                  className="ml-auto text-[10px] rounded-sm border-white/15 text-zinc-400"
                >
                  {a.proposer_agent}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </div>

      {dissenting_opinions?.length ? (
        <div className="mt-4 border-t border-white/5 pt-4">
          <div className="cre-overline mb-2 text-rose-300">Dissenting opinions</div>
          <ul className="space-y-1 text-sm text-zinc-400">
            {dissenting_opinions.map((d, i) => (
              <li
                key={i}
                data-testid={`dissent-${i}`}
                className="flex gap-2"
              >
                <span className="text-rose-500 font-mono">!</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
